import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../app/api';


// Define the initial state
const initialState = {
    expenses: [],
    otherExpenses: [],
    singleExpense: null,
    expensesData: [],
    loading: false,
    error: null,
};

export const addExpense = createAsyncThunk(
    'expenses/addExpense',
    async ({ date, number, client, typeOfPayment, comment, company_id, tableData }, { rejectWithValue }) => {
        try {
            const response = await api.post('/expense/expense', { 
                date, 
                number, 
                client, 
                typeOfPayment, 
                comment, 
                company_id, 
                tableData: tableData.map(item => ({
                    ...item,
                    total: item.qty * item.price,
                    total_with_vat: item.qty * item.price * (1 + item.dds / 100)
                }))
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }   
);

export const getAllExpenses = createAsyncThunk(
    'expenses/getAllExpenses',
    async ({ company_id }, { rejectWithValue }) => {
        try {
            const response = await api.post('/expense/expense/all', { company_id });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }   
);

export const getExpense = createAsyncThunk(
    'expenses/getExpense',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/expense/expense/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const updateExpense = createAsyncThunk(
    'expenses/updateExpense',
    async ({ id, date, number, client, typeOfPayment, comment, company_id, tableData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/expense/expense/${id}`, { 
                date, 
                number, 
                client, 
                typeOfPayment, 
                comment, 
                company_id, 
                tableData: tableData.map(item => ({
                    ...item,
                    total: item.qty * item.price,
                    total_with_vat: item.qty * item.price * (1 + item.dds / 100)
                }))
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }   
);

export const deleteExpense = createAsyncThunk(
    'expenses/deleteExpense',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/expense/expense/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }   
);

export const getExpensesData = createAsyncThunk(
    'expenses/getExpensesData',
    async ({ company_id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`/expense/expense/data/${company_id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const addOtherExpense = createAsyncThunk(
    'expenses/addOtherExpense',
    async ({ date, amount, comment, company_id, type_of_expense }, { rejectWithValue }) => {
        try {
            const response = await api.post('/expense/expense/other', { 
                date, 
                amount, 
                comment, 
                company_id, 
                type_of_expense
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const editOtherExpense = createAsyncThunk(
    'expenses/editOtherExpense',
    async ({ id, date, amount, comment, company_id, type_of_expense }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/expense/expense/other/${id}`, {
                date,
                amount,
                comment,
                company_id,
                type_of_expense
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const getOtherExpense = createAsyncThunk(
    'expenses/getOtherExpense',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/expense/expense/other/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'An error occurred');
        }
    }
);

export const getOtherExpenses = createAsyncThunk(
    'expenses/getOtherExpenses',
    async ({ company_id }, { rejectWithValue }) => {
        try {
            const response = await api.post('/expense/expense/other/all', { company_id });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred');
        }
    }
);

export const deleteOtherExpense = createAsyncThunk(
'expenses/deleteOtherExpense',
async (id, { rejectWithValue }) => {
    try {
        const response = await api.delete(`/expense/expense/other/${id}`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'An error occurred');
    }
});

// Define the slice
const expensesSlice = createSlice({
    name: 'expenses',
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(addExpense.pending, (state) => {
                state.loading = true;
            })
            .addCase(addExpense.fulfilled, (state, action) => {
                // state.expenses = action.payload;
                state.loading = false;
            })
            .addCase(addExpense.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(addOtherExpense.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addOtherExpense.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload && action.payload.otherExpense) {
                    state.otherExpenses = state.otherExpenses || [];
                    state.otherExpenses.push(action.payload.otherExpense);
                }
            })
            .addCase(addOtherExpense.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getOtherExpenses.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getOtherExpenses.fulfilled, (state, action) => {
                state.loading = false;
                state.otherExpenses = action.payload.otherExpenses;
            })
            .addCase(getOtherExpenses.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(editOtherExpense.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(editOtherExpense.fulfilled, (state, action) => {
                state.loading = false;
                state.otherExpenses = state.otherExpenses.map(otherExpense => otherExpense.id === action.payload.id ? action.payload.otherExpense : otherExpense);
            })
            .addCase(editOtherExpense.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(deleteOtherExpense.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteOtherExpense.fulfilled, (state, action) => {
                state.loading = false;
                state.otherExpenses = state.otherExpenses.filter(otherExpense => otherExpense.id !== action.payload.id);
            })
            .addCase(deleteOtherExpense.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getAllExpenses.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllExpenses.fulfilled, (state, action) => {
                state.expenses = action.payload.expenses;
                state.loading = false;
            })
            .addCase(getAllExpenses.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(deleteExpense.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteExpense.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(deleteExpense.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(updateExpense.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateExpense.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(updateExpense.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getExpense.pending, (state) => {
                state.loading = true;
            })
            .addCase(getExpense.fulfilled, (state, action) => {
                state.loading = false;
                state.singleExpense = action.payload.expense;
            })
            .addCase(getExpense.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            })
            .addCase(getExpensesData.pending, (state) => {
                state.loading = true;
            })
            .addCase(getExpensesData.fulfilled, (state, action) => {
                state.loading = false;
                state.expensesData = action.payload.expenseData;
            })
            .addCase(getExpensesData.rejected, (state, action) => {
                state.error = action.payload;
                state.loading = false;
            });
    },
});

// Export actions
export default expensesSlice.reducer;
